import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, CheckCircle } from 'lucide-react';
import { getErrorMessage } from '../utils/errorHelpers';

const StateSelectionStep = ({ onStateSelected, workflow = 'retail' }) => {
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAvailableStates();
  }, []);

  const fetchAvailableStates = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/states/available`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setAvailableStates(response.data.states || []);
      
      // Auto-select if only one state
      if (response.data.states && response.data.states.length === 1) {
        setSelectedState(response.data.states[0].code);
      }
      
    } catch (error) {
      console.error('Error fetching states:', error);
      const errorMsg = getErrorMessage(error, 'Failed to load available states');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (selectedState) {
      const stateInfo = availableStates.find(s => s.code === selectedState);
      onStateSelected(selectedState, stateInfo);
    }
  };

  const getStateEmoji = (stateCode) => {
    const emojis = {
      'TS': '🏛️',
      'KA': '🌳',
      'AP': '🌊',
      'TN': '🏺',
      'MH': '🏙️'
    };
    return emojis[stateCode] || '📍';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchAvailableStates}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (availableStates.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <MapPin className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <p className="text-yellow-800 mb-2 font-semibold">
            No states are enabled for your account.
          </p>
          <p className="text-yellow-600 text-sm">
            Please contact your administrator to enable states for {workflow} workflow.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select State</h2>
        <p className="text-gray-600">
          Choose the state for which you want to create a legal opinion.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {availableStates.map(state => (
          <div
            key={state.code}
            onClick={() => setSelectedState(state.code)}
            className={`
              border-2 rounded-lg p-6 cursor-pointer transition-all
              ${selectedState === state.code
                ? 'border-blue-600 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
              }
            `}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-semibold text-gray-900">{state.name}</h3>
              <span className="text-3xl">{getStateEmoji(state.code)}</span>
            </div>
            
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">State Code:</span>{' '}
                <span className="font-semibold text-gray-900">{state.code}</span>
              </p>
              <p>
                <span className="font-medium">Bank Formats:</span>{' '}
                {state.bank_formats?.length || 0} available
              </p>
            </div>

            {selectedState === state.code && (
              <div className="mt-4 flex items-center text-blue-600 font-semibold">
                <CheckCircle className="w-5 h-5 mr-2" />
                Selected
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Continue Button */}
      <button
        onClick={handleContinue}
        disabled={!selectedState}
        className={`
          w-full py-3 rounded-lg font-semibold transition-colors
          ${selectedState
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }
        `}
      >
        {selectedState ? 'Continue to Upload Documents' : 'Please select a state'}
      </button>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Tip:</strong> The state selection determines which document types and formats will be used for extraction.
          Make sure to select the correct state for your documents.
        </p>
      </div>
    </div>
  );
};

export default StateSelectionStep;
